// SubscriptionDashboard.jsx
// One-file React dashboard for DailySubscriptionSnapshot analytics
// Dependencies: react, axios, luxon, recharts, tailwindcss (for styling)

import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { DateTime } from "luxon";

/*************************************
 *  CONSTANTS & HELPERS
 *************************************/

// Semantic colour palette (align with Tailwind shades)
const COLORS = {
  active: "#2563EB", // blue-600
  pending: "#F97316", // orange-500
  newCreated: "#10B981", // emerald-500
  newStarted: "#60A5FA", // sky-400
  ended: "#EF4444", // red-500
  price: "#7C3AED", // violet-600
  duration: "#D97706", // amber-600
  pct11Plus: "#FBBF24", // yellow-400
  pct7Plus: "#34D399", // emerald-400
  ageActive: "#6366F1", // indigo-500 (kept for potential future use)
  ageEnded: "#6B7280", // gray-500
  // More distinct cohort colours
  cohorts: ["#1D4ED8", "#D946EF", "#059669", "#DB2777"],
};

// Normalise snapshot row -> DateTime objects, numbers
function normalise(row) {
  const d = { ...row };
  d.snapshot_date = DateTime.fromISO(row.snapshot_date);
  d.day_timestamp = row.day_timestamp
    ? DateTime.fromISO(row.day_timestamp)
    : d.snapshot_date;
  return d;
}

// Group by granularity key
function bucketKey(dt, granularity) {
  switch (granularity) {
    case "weekly":
      return `${dt.weekNumber}-${dt.year}`;
    case "monthly":
      return `${dt.month}-${dt.year}`;
    default:
      return dt.toISODate(); // daily
  }
}

// Reduction of bucket to aggregate row
function reduceBucket(rows) {
  if (!Array.isArray(rows)) rows = [rows]; // ensure array safety

  // Use last row for point‑in‑time values
  const last = rows[rows.length - 1];

  // Summations
  const sum = (key) => rows.reduce((t, r) => t + (r[key] || 0), 0);

  // Average helpers
  const avg = (key) => sum(key) / rows.length;

  return {
    snapshot_date: last.snapshot_date,
    // Point‑in‑time counts
    active_subscriptions_count: last.active_subscriptions_count,

    // Flows & averages over the bucket
    pending_returns_count: avg("pending_returns_count"),
    new_subscriptions_created_yesterday: sum(
      "new_subscriptions_created_yesterday"
    ),
    new_subscriptions_started_yesterday: sum(
      "new_subscriptions_started_yesterday"
    ),
    subscriptions_ended_yesterday: sum("subscriptions_ended_yesterday"),
    avg_subscription_price: avg("avg_subscription_price"),
    avg_subscription_duration: avg("avg_subscription_duration"),

    // Duration bands (point‑in‑time)
    subscriptions_with_duration_11_plus:
      last.subscriptions_with_duration_11_plus,
    subscriptions_with_duration_7_plus: last.subscriptions_with_duration_7_plus,

    pct_11_plus:
      last.active_subscriptions_count > 0
        ? last.subscriptions_with_duration_11_plus /
          last.active_subscriptions_count
        : 0,
    pct_7_plus:
      last.active_subscriptions_count > 0
        ? last.subscriptions_with_duration_7_plus /
          last.active_subscriptions_count
        : 0,

    // Customer age (averaged within bucket)
    avg_customer_age_active_subscriptions: avg(
      "avg_customer_age_active_subscriptions"
    ),
    avg_customer_age_ended_subscriptions: avg(
      "avg_customer_age_ended_subscriptions"
    ),

    // Start‑year cohorts & first‑time customer counts (point‑in‑time, like Active Subs)
    active_subscriptions_start_year: last.active_subscriptions_start_year,
    first_time_customers_by_year: last.first_time_customers_by_year,

    // Net adds over the bucket
    net_adds:
      sum("new_subscriptions_started_yesterday") -
      sum("subscriptions_ended_yesterday"),
  };
}

/*************************************
 *  REUSABLE UI PIECES
 *************************************/

// Simple KPI Card with sparkline capability (sparkline omitted for brevity)
function KpiCard({ title, value, suffix = "", delta }) {
  const formatted =
    typeof value === "number"
      ? value.toLocaleString(undefined, {
          maximumFractionDigits: 1,
        })
      : value;
  const deltaColor =
    delta > 0 ? "text-green-600" : delta < 0 ? "text-red-600" : "text-gray-500";
  return (
    <div className="flex flex-col bg-white border rounded-lg shadow-sm p-4 w-full">
      <span className="text-sm font-medium text-gray-500 mb-1">{title}</span>
      <span className="text-2xl font-bold text-gray-800 leading-none">
        {formatted}
        {suffix}
      </span>
      {delta !== undefined && (
        <span className={`text-xs font-medium ${deltaColor}`}>
          {delta > 0 ? "+" : ""}
          {delta.toFixed(1)}%
        </span>
      )}
    </div>
  );
}

/*************************************
 *  MAIN DASHBOARD COMPONENT
 *************************************/

export default function SubscriptionDashboard() {
  // State
  const [rawSnapshots, setRawSnapshots] = useState([]);
  const [granularity, setGranularity] = useState("daily"); // daily / weekly / monthly
  const [startDate, setStartDate] = useState(
    DateTime.local().minus({ days: 90 }).toISODate()
  );
  const [endDate, setEndDate] = useState(DateTime.local().toISODate());

  // Fetch once
  useEffect(() => {
    async function fetchData() {
      try {
        const res = await axios.get("/api/subscription/snapshots");
        if (Array.isArray(res.data)) {
          const normalized = res.data.map(normalise);
          setRawSnapshots(
            normalized.sort((a, b) => a.snapshot_date - b.snapshot_date)
          );
        }
      } catch (err) {
        console.error("Error fetching subscription snapshots", err);
      }
    }
    fetchData();
  }, []);

  // Filter by date range
  const filtered = useMemo(() => {
    const s = DateTime.fromISO(startDate);
    const e = DateTime.fromISO(endDate);
    return rawSnapshots.filter(
      (r) => r.snapshot_date >= s && r.snapshot_date <= e
    );
  }, [rawSnapshots, startDate, endDate]);

  // Aggregate by granularity
  const aggregated = useMemo(() => {
    if (granularity === "daily") return filtered.map(reduceBucket); // each row alone
    const buckets = new Map();
    filtered.forEach((r) => {
      const key = bucketKey(r.snapshot_date, granularity);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(r);
    });
    return Array.from(buckets.values())
      .map(reduceBucket)
      .sort((a, b) => a.snapshot_date - b.snapshot_date);
  }, [filtered, granularity]);

  // Latest snapshot for KPI
  const latest = aggregated.length ? aggregated[aggregated.length - 1] : null;
  const prev = aggregated.length > 1 ? aggregated[aggregated.length - 2] : null;

  // Delta helper
  const pctDelta = (curr, prev) =>
    prev && prev !== 0 ? ((curr - prev) / prev) * 100 : 0;

  /*************** Charts Data ***************/
  // Chart: Active + Pending Returns
  const chartActive = aggregated.map((r) => ({
    date: r.snapshot_date.toISODate(),
    active: r.active_subscriptions_count,
  }));

  // Chart: Flow New vs Started vs Ended
  const chartFlow = aggregated.map((r) => ({
    date: r.snapshot_date.toISODate(),
    created: r.new_subscriptions_created_yesterday,
    started: r.new_subscriptions_started_yesterday,
    ended: r.subscriptions_ended_yesterday,
  }));

  // Chart: Avg Price
  const chartPrice = aggregated.map((r) => ({
    date: r.snapshot_date.toISODate(),
    price: r.avg_subscription_price,
  }));

  // Chart: Duration & % ≥11 / 7 mo
  const chartDuration = aggregated.map((r) => ({
    date: r.snapshot_date.toISODate(),
    duration: r.avg_subscription_duration,
    pct11: r.pct_11_plus * 100,
    pct7: r.pct_7_plus * 100,
  }));

  // Chart: Cohort by Start Year (stacked area absolute)
  const cohortYears = ["2022", "2023", "2024", "2025"];
  const chartCohort = aggregated.map((r) => {
    const obj = { date: r.snapshot_date.toISODate() };
    cohortYears.forEach((y) => {
      obj[y] = r.active_subscriptions_start_year[y] || 0;
    });
    return obj;
  });

  // Chart: First-time Customers by Year (bar)
  const chartFirstTime = aggregated.map((r) => {
    const obj = { date: r.snapshot_date.toISODate() };
    cohortYears.forEach((y) => {
      obj[y] = r.first_time_customers_by_year[y] || 0;
    });
    return obj;
  });

  /*************** RENDER ***************/
  return (
    <div className="w-full min-h-screen px-4 pt-8 pb-11 bg-gray-50">
      <div className="bg-white shadow-sm border rounded-lg p-6 space-y-12 h-full">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">
            Subscription Analytics
          </h1>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Granularity */}
            <div className="flex flex-col">
              <label
                htmlFor="granularity"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                Granularity
              </label>
              <select
                id="granularity"
                value={granularity}
                onChange={(e) => setGranularity(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            {/* Start Date */}
            <div className="flex flex-col">
              <label
                htmlFor="startDate"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                Start Date
              </label>
              <input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {/* End Date */}
            <div className="flex flex-col">
              <label
                htmlFor="endDate"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                End Date
              </label>
              <input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* KPI Section */}
        {latest && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <KpiCard
              title="Active Subs"
              value={latest.active_subscriptions_count}
              delta={pctDelta(
                latest.active_subscriptions_count,
                prev?.active_subscriptions_count
              )}
            />
            <KpiCard
              title="Net Adds"
              value={latest.net_adds}
              delta={pctDelta(latest.net_adds, prev?.net_adds)}
            />
            <KpiCard
              title="Avg Price"
              value={latest.avg_subscription_price}
              suffix=" kr"
              delta={pctDelta(
                latest.avg_subscription_price,
                prev?.avg_subscription_price
              )}
            />
            <KpiCard
              title="Avg Duration"
              value={latest.avg_subscription_duration}
              suffix=" mo"
              delta={pctDelta(
                latest.avg_subscription_duration,
                prev?.avg_subscription_duration
              )}
            />
            <KpiCard
              title="Avg Cust Age (Act)"
              value={latest.avg_customer_age_active_subscriptions}
              suffix=" mo"
              delta={pctDelta(
                latest.avg_customer_age_active_subscriptions,
                prev?.avg_customer_age_active_subscriptions
              )}
            />
            <KpiCard
              title="Avg Cust Age (End)"
              value={latest.avg_customer_age_ended_subscriptions}
              suffix=" mo"
              delta={pctDelta(
                latest.avg_customer_age_ended_subscriptions,
                prev?.avg_customer_age_ended_subscriptions
              )}
            />
          </div>
        )}

        {/* Active + Pending Returns */}
        <section>
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            Active Subscriptions & Pending Returns
          </h2>
          <div className="w-full h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartActive}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="date"
                  stroke="#4B5563"
                  interval="preserveStartEnd"
                />
                <YAxis yAxisId="left" stroke={COLORS.active} />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="active"
                  stroke={COLORS.active}
                  strokeWidth={2}
                  dot={false}
                  name="Active Subs"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Flow New vs Started vs Ended */}
        <section>
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            Daily Flow (Created / Started / Ended)
          </h2>
          <div className="w-full h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartFlow}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="date"
                  stroke="#4B5563"
                  interval="preserveStartEnd"
                />
                <YAxis stroke="#4B5563" />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="created"
                  stackId="flow"
                  fill={COLORS.newCreated}
                  name="Created"
                />
                <Bar
                  dataKey="started"
                  stackId="flow"
                  fill={COLORS.newStarted}
                  name="Started"
                />
                <Bar
                  dataKey="ended"
                  stackId="flow"
                  fill={COLORS.ended}
                  name="Ended"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Cohorts & First-time Customers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Cohort Start Year */}
          <section>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Active Subs by Start Year
            </h2>
            <div className="w-full h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={chartCohort}
                  margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="date"
                    stroke="#4B5563"
                    interval="preserveStartEnd"
                  />
                  <YAxis stroke="#4B5563" />
                  <Tooltip />
                  <Legend />
                  {cohortYears.map((y, idx) => (
                    <Area
                      key={y}
                      type="monotone"
                      dataKey={y}
                      stackId="1"
                      stroke={COLORS.cohorts[idx]}
                      fill={COLORS.cohorts[idx]}
                      name={y}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* First-time Customers */}
          <section>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              First-time Customers by Year
            </h2>
            <div className="w-full h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartFirstTime}
                  margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="date"
                    stroke="#4B5563"
                    interval="preserveStartEnd"
                  />
                  <YAxis stroke="#4B5563" />
                  <Tooltip />
                  <Legend />
                  {cohortYears.map((y, idx) => (
                    <Bar
                      key={y}
                      dataKey={y}
                      stackId="ftc"
                      fill={COLORS.cohorts[idx]}
                      name={y}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        </div>

        {/* Financials & Duration */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Avg Price */}
          <section>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Average Subscription Price
            </h2>
            <div className="w-full h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartPrice}
                  margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="date"
                    stroke="#4B5563"
                    interval="preserveStartEnd"
                  />
                  <YAxis stroke="#4B5563" domain={[1000, "auto"]} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke={COLORS.price}
                    strokeWidth={2}
                    dot={false}
                    name="Avg Price (kr)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Duration & % 11+ / 7+ */}
          <section>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Avg Duration & % ≥12mo / ≥8mo
            </h2>
            <div className="w-full h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartDuration}
                  margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="date"
                    stroke="#4B5563"
                    interval="preserveStartEnd"
                  />
                  <YAxis yAxisId="left" stroke={COLORS.duration} />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke={COLORS.pct11Plus}
                  />
                  <Tooltip />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="duration"
                    stroke={COLORS.duration}
                    strokeWidth={2}
                    dot={false}
                    name="Avg Duration (mo)"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="pct11"
                    stroke={COLORS.pct11Plus}
                    strokeDasharray="5 5"
                    dot={false}
                    name="% ≥12mo"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="pct7"
                    stroke={COLORS.pct7Plus}
                    strokeDasharray="2 2"
                    dot={false}
                    name="% ≥8mo"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        </div>

        {/* Raw Data Table */}
        <section>
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            Snapshot Table
          </h2>
          <div className="overflow-x-auto max-h-[600px] border rounded-lg">
            <table className="min-w-full text-sm text-gray-700 divide-y divide-gray-200">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-left font-semibold">Active</th>
                  <th className="px-3 py-2 text-left font-semibold">Pending</th>
                  <th className="px-3 py-2 text-left font-semibold">Created</th>
                  <th className="px-3 py-2 text-left font-semibold">Started</th>
                  <th className="px-3 py-2 text-left font-semibold">Ended</th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Avg Price
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">Avg Dur</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {aggregated.map((r) => (
                  <tr
                    key={r.snapshot_date.toISO()}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.snapshot_date.toFormat("yyyy-LL-dd")}
                    </td>
                    <td className="px-3 py-2">
                      {r.active_subscriptions_count}
                    </td>
                    <td className="px-3 py-2">
                      {r.pending_returns_count.toFixed(0)}
                    </td>
                    <td className="px-3 py-2">
                      {r.new_subscriptions_created_yesterday}
                    </td>
                    <td className="px-3 py-2">
                      {r.new_subscriptions_started_yesterday}
                    </td>
                    <td className="px-3 py-2">
                      {r.subscriptions_ended_yesterday}
                    </td>
                    <td className="px-3 py-2">
                      {r.avg_subscription_price.toFixed(0)}
                    </td>
                    <td className="px-3 py-2">
                      {r.avg_subscription_duration.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
